<script setup lang="ts">
import { getLocalTypeIconSrc, fallbackTypeIcon } from "../ui/typeIcon";
import { onBeforeUnmount, ref } from "vue";
import { Side } from "../core/round";

export interface SkillCastPayload {
    side: Side;
    skillName: string;
    attributeName: string;
    attributeIconUrl: string;
    criticalHit: boolean;
}

type AnimationPhase = "hidden" | "entering" | "holding" | "leaving";

const payload = ref<SkillCastPayload | null>(null);
const phase = ref<AnimationPhase>("hidden");

let playbackId = 0;
const pendingTimers = new Map<ReturnType<typeof setTimeout>, () => void>();

function prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
        const timer = setTimeout(() => {
            pendingTimers.delete(timer);
            resolve();
        }, ms);
        pendingTimers.set(timer, resolve);
    });
}

function clearTimers() {
    for (const [timer, resolve] of pendingTimers) {
        clearTimeout(timer);
        resolve();
    }
    pendingTimers.clear();
}

function cancel() {
    playbackId++;
    clearTimers();
    phase.value = "hidden";
    payload.value = null;
}

async function play(nextPayload: SkillCastPayload): Promise<void> {
    cancel();
    const currentPlayback = playbackId;
    const reducedMotion = prefersReducedMotion();

    payload.value = nextPayload;
    phase.value = reducedMotion ? "holding" : "entering";

    if (!reducedMotion) {
        await wait(220);
        if (currentPlayback !== playbackId) return;
        phase.value = "holding";
    }

    await wait(reducedMotion ? 600 : 850);
    if (currentPlayback !== playbackId) return;

    if (!reducedMotion) {
        phase.value = "leaving";
        await wait(180);
        if (currentPlayback !== playbackId) return;
    }

    phase.value = "hidden";
    payload.value = null;
}

function accessibleLabel(): string {
    if (!payload.value) return "";
    const criticalText = payload.value.criticalHit ? "，致命一击" : "";
    return payload.value.skillName + "，" + payload.value.attributeName + criticalText;
}

onBeforeUnmount(cancel);

defineExpose({ play, cancel });
</script>

<template>
    <div class="skill-cast-row" aria-live="polite" aria-atomic="true">
        <div
            v-if="payload && phase !== 'hidden'"
            class="skill-banner"
            :class="[
                'side-' + payload.side,
                'phase-' + phase,
                { critical: payload.criticalHit },
            ]"
            :aria-label="accessibleLabel()"
        >
            <div v-if="payload.criticalHit" class="fire-layer" aria-hidden="true">
                <span
                    v-for="i in 7"
                    :key="'flame-' + i"
                    class="flame"
                ></span>
                <span
                    v-for="i in 8"
                    :key="'spark-' + i"
                    class="spark"
                ></span>
            </div>

            <div class="banner-content">
                <div class="attribute">
                    <img :src="getLocalTypeIconSrc(payload.attributeName)" :data-fallback-src="payload.attributeIconUrl" @error="fallbackTypeIcon" alt="" />
                    <span>{{ payload.attributeName }}</span>
                </div>
                <strong class="skill-name">{{ payload.skillName }}</strong>
            </div>
        </div>
    </div>
</template>

<style scoped>
.skill-cast-row {
    position: relative;
    flex: 0 0 calc(var(--ui-unit) * 72);
    width: 100%;
    height: calc(var(--ui-unit) * 72);
    padding: calc(var(--ui-unit) * 8) calc(var(--ui-unit) * 10);
    box-sizing: border-box;
    pointer-events: none;
    clip-path: inset(calc(var(--ui-unit) * -18) 0 calc(var(--ui-unit) * -18) 0);
}

.skill-banner {
    position: relative;
    display: flex;
    align-items: center;
    width: min(calc(var(--ui-unit) * 520), calc(100% - calc(var(--ui-unit) * 20)));
    height: calc(var(--ui-unit) * 56);
    box-sizing: border-box;
    color: #f5f5f5;
    background: rgba(13, 14, 22, 0.94);
    border: calc(var(--ui-unit) * 1) solid rgba(255, 215, 0, 0.72);
    border-radius: calc(var(--ui-unit) * 6);
    box-shadow: 0 calc(var(--ui-unit) * 8) calc(var(--ui-unit) * 24) rgba(0, 0, 0, 0.38),
        inset calc(var(--ui-unit) * 4) 0 0 #ffd700;
    will-change: transform, opacity;
}

.skill-banner.side-left {
    margin-right: auto;
}

.skill-banner.side-right {
    margin-left: auto;
    box-shadow: 0 calc(var(--ui-unit) * 8) calc(var(--ui-unit) * 24) rgba(0, 0, 0, 0.38),
        inset calc(var(--ui-unit) * -4) 0 0 #ffd700;
}

.skill-banner.side-left.phase-entering {
    animation: slide-in-left 220ms cubic-bezier(0.2, 0.75, 0.2, 1) both;
}

.skill-banner.side-right.phase-entering {
    animation: slide-in-right 220ms cubic-bezier(0.2, 0.75, 0.2, 1) both;
}

.skill-banner.side-left.phase-leaving {
    animation: slide-out-left 180ms ease-in both;
}

.skill-banner.side-right.phase-leaving {
    animation: slide-out-right 180ms ease-in both;
}

.banner-content {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: calc(var(--ui-unit) * 16);
    min-width: 0;
    width: 100%;
    padding: calc(var(--ui-unit) * 7) calc(var(--ui-unit) * 18);
    box-sizing: border-box;
}

.side-right .banner-content {
    flex-direction: row-reverse;
    text-align: right;
}

.attribute {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    gap: calc(var(--ui-unit) * 6);
    min-width: calc(var(--ui-unit) * 72);
    color: #ffd970;
    font-size: calc(var(--ui-unit) * 15);
    font-weight: 700;
}

.attribute img {
    width: calc(var(--ui-unit) * 30);
    height: calc(var(--ui-unit) * 30);
    object-fit: contain;
    filter: drop-shadow(0 0 calc(var(--ui-unit) * 5) rgba(255, 255, 255, 0.24));
}

.skill-name {
    min-width: 0;
    overflow: hidden;
    color: #ffffff;
    font-size: calc(var(--ui-unit) * 27);
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-shadow: 0 calc(var(--ui-unit) * 2) calc(var(--ui-unit) * 8) rgba(0, 0, 0, 0.72);
}

.skill-banner.critical {
    border-color: #ff8a1f;
    box-shadow: 0 0 calc(var(--ui-unit) * 20) rgba(255, 92, 20, 0.58),
        0 calc(var(--ui-unit) * 8) calc(var(--ui-unit) * 24) rgba(0, 0, 0, 0.42), inset calc(var(--ui-unit) * 4) 0 0 #ffcf33;
}

.skill-banner.critical.side-right {
    box-shadow: 0 0 calc(var(--ui-unit) * 20) rgba(255, 92, 20, 0.58),
        0 calc(var(--ui-unit) * 8) calc(var(--ui-unit) * 24) rgba(0, 0, 0, 0.42), inset calc(var(--ui-unit) * -4) 0 0 #ffcf33;
}

.fire-layer {
    position: absolute;
    z-index: 1;
    inset: calc(var(--ui-unit) * -14) 0 0;
    overflow: hidden;
    border-radius: calc(var(--ui-unit) * 6);
}

.side-right .fire-layer {
    transform: scaleX(-1);
}

.flame,
.spark {
    position: absolute;
    display: block;
    background: #ff6a1a;
}

.flame {
    bottom: 0;
    width: calc(var(--ui-unit) * 18);
    height: calc(var(--ui-unit) * 34);
    clip-path: polygon(50% 0, 88% 42%, 100% 100%, 0 100%, 14% 44%);
    transform-origin: 50% 100%;
    animation: flame-rise 540ms ease-in-out infinite alternate;
}

.flame:nth-child(1) {
    left: 4%;
    height: calc(var(--ui-unit) * 28);
}

.flame:nth-child(2) {
    left: 18%;
    background: #ffb21a;
    animation-delay: -120ms;
}

.flame:nth-child(3) {
    left: 35%;
    height: calc(var(--ui-unit) * 25);
    animation-delay: -260ms;
}

.flame:nth-child(4) {
    left: 52%;
    height: calc(var(--ui-unit) * 38);
    background: #ffb21a;
    animation-delay: -80ms;
}

.flame:nth-child(5) {
    left: 68%;
    height: calc(var(--ui-unit) * 29);
    animation-delay: -310ms;
}

.flame:nth-child(6) {
    left: 83%;
    background: #ffb21a;
    animation-delay: -190ms;
}

.flame:nth-child(7) {
    left: 94%;
    height: calc(var(--ui-unit) * 24);
    animation-delay: -360ms;
}

.spark {
    bottom: calc(var(--ui-unit) * 8);
    width: calc(var(--ui-unit) * 4);
    height: calc(var(--ui-unit) * 4);
    border-radius: 50%;
    background: #ffe36b;
    animation: spark-fly 680ms ease-out infinite;
}

.spark:nth-child(8) {
    left: 9%;
}

.spark:nth-child(9) {
    left: 22%;
    animation-delay: -180ms;
}

.spark:nth-child(10) {
    left: 38%;
    animation-delay: -420ms;
}

.spark:nth-child(11) {
    left: 49%;
    animation-delay: -90ms;
}

.spark:nth-child(12) {
    left: 61%;
    animation-delay: -310ms;
}

.spark:nth-child(13) {
    left: 73%;
    animation-delay: -520ms;
}

.spark:nth-child(14) {
    left: 86%;
    animation-delay: -240ms;
}

.spark:nth-child(15) {
    left: 96%;
    animation-delay: -470ms;
}

@keyframes slide-in-left {
    from {
        opacity: 0;
        transform: translateX(calc(-100% - calc(var(--ui-unit) * 32)));
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes slide-in-right {
    from {
        opacity: 0;
        transform: translateX(calc(100% + calc(var(--ui-unit) * 32)));
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes slide-out-left {
    from {
        opacity: 1;
        transform: translateX(0);
    }
    to {
        opacity: 0;
        transform: translateX(calc(var(--ui-unit) * 28));
    }
}

@keyframes slide-out-right {
    from {
        opacity: 1;
        transform: translateX(0);
    }
    to {
        opacity: 0;
        transform: translateX(calc(var(--ui-unit) * -28));
    }
}

@keyframes flame-rise {
    from {
        opacity: 0.66;
        transform: scaleY(0.68) skewX(-5deg);
    }
    to {
        opacity: 0.96;
        transform: scaleY(1.08) skewX(5deg);
    }
}

@keyframes spark-fly {
    from {
        opacity: 0;
        transform: translate(0, 0) scale(0.7);
    }
    30% {
        opacity: 1;
    }
    to {
        opacity: 0;
        transform: translate(calc(var(--ui-unit) * 18), calc(var(--ui-unit) * -42)) scale(0.2);
    }
}



@media (prefers-reduced-motion: reduce) {
    .skill-banner {
        animation: none !important;
    }

    .flame,
    .spark {
        animation: none;
    }

    .flame {
        opacity: 0.78;
        transform: scaleY(0.72);
    }

    .spark {
        display: none;
    }
}


</style>
